import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Divider, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  styled
} from '@mui/material';
import { formatCurrency, numberToWords } from '../../utils/formatters';

// Interfaces
export interface BusinessDetails {
  name: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  gstin?: string; // Optional as per requirement
  phone: string;
  email: string;
  bankName: string;
  bankBranch: string;
  accountNumber: string;
  ifsc: string;
  upiId?: string;
  upiQrImage?: string;
  logoUrl?: string;
  gstRegistered: string;
  gstType: string;
}

export interface ClientDetails {
  name: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  gstin?: string; // Optional as per requirement
  phone: string;
  email: string;
}

export interface DeliveryDetails {
  name: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  phone: string;
  email: string;
  contactPerson: string;
  contactNumber: string;
}

export interface InvoiceItem {
  productName: string;
  description?: string;
  quantity: number;
  unit?: string;
  hsn: string;
  gstRate: number;
  taxableValue: number;
  taxAmount: number;
  total: number;
}

export interface InvoiceDetails {
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  purchaseRefNumber?: string;
  placeOfSupply?: string;
  discount?: number;
  additionalCharges?: {
    name: string;
    amount: number;
  }[];
  termsAndConditions?: string[];
}

export interface AkMoInvoicingFormatProps {
  businessDetails: BusinessDetails;
  clientDetails: ClientDetails;
  deliveryDetails?: DeliveryDetails;
  invoiceDetails: InvoiceDetails;
  items: InvoiceItem[];
  primaryColor?: string;
  secondaryColor?: string;
  fontColor?: string;
  primaryFontColor?: string;
  backgroundStyle?: 'gradient' | 'normal';
}

// Styled Components with dynamic colors
const InvoicePaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  width: '210mm', // A4 width
  backgroundColor: '#fff',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '285mm', // A4 height with minimal reduction for padding (297mm - 12mm)
  '@media print': {
    boxShadow: 'none',
    width: '100%',
    height: 'auto',
    pageBreakAfter: 'avoid',
    pageBreakInside: 'avoid',
  }
}));

const BorderLine = styled(Divider)(({ theme }) => ({
  height: '0.70mm',
  background: `var(--primary-color, linear-gradient(135deg, #4776E6 0%, #8E54E9 100%))`, // Use CSS variables with fallback
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const AkMoInvoicingFormat: React.FC<AkMoInvoicingFormatProps> = ({
  businessDetails,
  clientDetails,
  deliveryDetails,
  invoiceDetails,
  items,
  primaryColor = '#4776E6',
  secondaryColor = '#8E54E9',
  fontColor = '#333333',
  primaryFontColor = '#FFFFFF',
  backgroundStyle = 'gradient',
}) => {
  // Create the gradient string for background elements
  const gradientStyle = backgroundStyle === 'gradient' 
    ? `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`
    : `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor} 100%)`;
  
  // Create a gradient that only uses primary color for table headers and certain UI elements
  const primaryOnlyStyle = `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor} 100%)`;
  
  // Add CSS variables for colors to the root element via style attributes
  React.useEffect(() => {
    // Set CSS variables for colors at the root level
    document.documentElement.style.setProperty('--primary-color', primaryColor);
    document.documentElement.style.setProperty('--secondary-color', secondaryColor);
    document.documentElement.style.setProperty('--font-color', fontColor);
    document.documentElement.style.setProperty('--primary-font-color', primaryFontColor);
    
    return () => {
      // Clean up CSS variables when component unmounts
      document.documentElement.style.removeProperty('--primary-color');
      document.documentElement.style.removeProperty('--secondary-color');
      document.documentElement.style.removeProperty('--font-color');
      document.documentElement.style.removeProperty('--primary-font-color');
    };
  }, [primaryColor, secondaryColor, fontColor, primaryFontColor]);

  // Use client details for delivery if not provided
  const finalDeliveryDetails = deliveryDetails || {
    name: clientDetails.name,
    address: clientDetails.address,
    city: clientDetails.city,
    state: clientDetails.state,
    pincode: clientDetails.pincode,
    phone: clientDetails.phone,
    email: clientDetails.email,
  };

  // Calculate totals
  const calculateTotals = () => {
    const totals = items.reduce((acc, item) => {
      return {
        totalTaxableValue: acc.totalTaxableValue + item.taxableValue,
        totalTaxAmount: acc.totalTaxAmount + item.taxAmount,
        totalAmount: acc.totalAmount + item.total,
      };
    }, { totalTaxableValue: 0, totalTaxAmount: 0, totalAmount: 0 });
    
    // Apply discount if available
    if (invoiceDetails.discount) {
      totals.totalAmount -= invoiceDetails.discount;
    }
    
    // Add additional charges if available
    const additionalChargesTotal = invoiceDetails.additionalCharges?.reduce((sum, charge) => sum + charge.amount, 0) || 0;
    totals.totalAmount += additionalChargesTotal;
    
    return {
      ...totals,
      additionalChargesTotal,
    };
  };
  
  const totals = calculateTotals();

  // Determine if interstate supply based on GSTN
  const isInterStateSupply = () => {
    if (!businessDetails.gstin || !clientDetails.gstin) return false;
    
    const sellerStateCode = businessDetails.gstin.substring(0, 2);
    const buyerStateCode = clientDetails.gstin.substring(0, 2);
    
    return sellerStateCode !== buyerStateCode;
  };

  const isInterState = isInterStateSupply();

  // Calculate CGST, SGST, IGST
  const calculateGST = () => {
    if (isInterState) {
      return {
        cgst: 0,
        sgst: 0,
        igst: totals.totalTaxAmount,
      };
    } else {
      return {
        cgst: totals.totalTaxAmount / 2,
        sgst: totals.totalTaxAmount / 2,
        igst: 0,
      };
    }
  };

  const gstValues = calculateGST();

  return (
    <InvoicePaper>
      {/* Main content wrapper */}
      <Box sx={{ flex: '1 0 auto', color: fontColor }}>
        {/* Header Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          {/* Business Details - Top Left */}
          <Box sx={{ maxWidth: '60%' }}>
            <Box sx={{ 
              background: primaryOnlyStyle,
              color: primaryFontColor,
              p: 0.5,
              borderRadius: 0.5 
            }}>
              <Typography variant="subtitle2" fontWeight="bold" fontFamily="'Segoe UI', Arial, sans-serif">
                {businessDetails.name}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ my: 0.5, color: fontColor }}>
              {businessDetails.address}, {businessDetails.city}<br />
              {businessDetails.state} - {businessDetails.pincode}
            </Typography>
            <Typography variant="body2" sx={{ color: fontColor }}>
              <strong>Phone:</strong> {businessDetails.phone}
            </Typography>
            <Typography variant="body2" sx={{ color: fontColor }}>
              <strong>Email:</strong> {businessDetails.email}
            </Typography>
            {businessDetails.gstin && (
              <Typography variant="body2" sx={{ color: fontColor }}>
                <strong>GSTN:</strong> {businessDetails.gstin}
              </Typography>
            )}
          </Box>

          {/* Invoice Info */}
          <Box sx={{ textAlign: 'right', minWidth: '40%' }}>
            {businessDetails.gstin && businessDetails.gstRegistered === 'registered' && businessDetails.gstType === 'regular' && (
              <Box sx={{
                background: primaryOnlyStyle,
                color: primaryFontColor,
                p: 1, 
                borderRadius: 0.5,
                mb: 1
              }}>
                <Typography variant="subtitle2" fontWeight="bold" fontFamily="'Segoe UI', Arial, sans-serif">
                  TAX INVOICE
                </Typography>
              </Box>
            )}
            <Typography variant="body2" sx={{ mt: 1, color: fontColor }}>
              <strong>Invoice Number:</strong> {invoiceDetails.invoiceNumber}
            </Typography>
            <Typography variant="body2" sx={{ color: fontColor }}>
              <strong>Invoice Date:</strong> {invoiceDetails.invoiceDate}
            </Typography>
            <Typography variant="body2" sx={{ color: fontColor }}>
              <strong>Due Date:</strong> {invoiceDetails.dueDate}
            </Typography>
            {invoiceDetails.purchaseRefNumber && (
              <Typography variant="body2" sx={{ color: fontColor }}>
                <strong>Purchase Ref. Number:</strong> {invoiceDetails.purchaseRefNumber}
              </Typography>
            )}
            <Typography variant="body2" sx={{ color: fontColor }}>
              <strong>Place of Supply:</strong> {invoiceDetails.placeOfSupply || businessDetails.state}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ 
          height: '0.5mm',
          background: gradientStyle,
          mb: 1,
          mt: 1,
        }} />

        {/* Client and Delivery Details */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          {/* Client Details - Left */}
          <Box sx={{ width: '48%' }}>
            <Typography variant="subtitle2" fontWeight="bold" fontFamily="'Segoe UI', Arial, sans-serif" sx={{ color: fontColor }}>
              Bill To:
            </Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ color: fontColor }}>
              {clientDetails.name}
            </Typography>
            <Typography variant="body2" sx={{ color: fontColor }}>
              {clientDetails.address}, {clientDetails.city}<br />
              {clientDetails.state} - {clientDetails.pincode}
            </Typography>
            <Typography variant="body2" sx={{ color: fontColor }}>
              <strong>Phone:</strong> {clientDetails.phone}
            </Typography>
            <Typography variant="body2" sx={{ color: fontColor }}>
              <strong>Email:</strong> {clientDetails.email}
            </Typography>
            {clientDetails.gstin ? (
              <Typography variant="body2" sx={{ color: fontColor }}>
                <strong>GSTN:</strong> {clientDetails.gstin}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: fontColor }}>
                <strong>Type:</strong> B2C
              </Typography>
            )}
          </Box>

          {/* Delivery Details - Right */}
          <Box sx={{ width: '48%' }}>
            <Typography variant="subtitle2" fontWeight="bold" fontFamily="'Segoe UI', Arial, sans-serif" sx={{ color: fontColor }}>
              Ship To:
            </Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ color: fontColor }}>
              {finalDeliveryDetails.name}
            </Typography>
            <Typography variant="body2" sx={{ color: fontColor }}>
              {finalDeliveryDetails.address}, {finalDeliveryDetails.city}<br />
              {finalDeliveryDetails.state} - {finalDeliveryDetails.pincode}
            </Typography>
            <Typography variant="body2" sx={{ color: fontColor }}>
              <strong>Phone:</strong> {finalDeliveryDetails.phone}
            </Typography>
            <Typography variant="body2" sx={{ color: fontColor }}>
              <strong>Email:</strong> {finalDeliveryDetails.email}
            </Typography>
          </Box>
        </Box>

        {/* Items Table */}
        <TableContainer sx={{ mb: 1.5, borderRadius: '4px', overflow: 'hidden' }}>
          <Table size="small" sx={{ 
            '& .MuiTableCell-root': { py: 0.5, px: 1, color: fontColor },
            '& .MuiTableCell-head': { color: primaryFontColor },
            borderCollapse: 'separate',
            borderSpacing: 0,
            overflow: 'hidden',
            '& .MuiTableHead-root': {
              '& .MuiTableCell-head:first-of-type': {
                borderTopLeftRadius: '4px',
              },
              '& .MuiTableCell-head:last-child': {
                borderTopRightRadius: '4px',
              }
            },
            '& .MuiTableBody-root': {
              '& tr:last-child .MuiTableCell-body:first-of-type': {
                borderBottomLeftRadius: '4px',
              },
              '& tr:last-child .MuiTableCell-body:last-child': {
                borderBottomRightRadius: '4px',
              }
            }
          }}>
            <TableHead sx={{ 
              background: primaryOnlyStyle,
              '& .MuiTableCell-head': {
                color: primaryFontColor,
                fontWeight: 'bold',
              }
            }}>
              <TableRow>
                <TableCell width="5%">No.</TableCell>
                <TableCell width="25%">Product/Service</TableCell>
                <TableCell width="8%" align="center">Qty</TableCell>
                <TableCell width="12%" align="center">HSN Code</TableCell>
                <TableCell width="10%" align="center">GST Rate</TableCell>
                <TableCell width="12%" align="right">Taxable Value</TableCell>
                <TableCell width="12%" align="right">Tax Amount</TableCell>
                <TableCell width="15%" align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' } }}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">{item.productName}</Typography>
                    {item.description && (
                      <Typography variant="caption" color="text.secondary">{item.description}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {item.quantity} {item.unit || 'Nos'}
                  </TableCell>
                  <TableCell align="center">{item.hsn}</TableCell>
                  <TableCell align="center">{item.gstRate}%</TableCell>
                  <TableCell align="right">{formatCurrency(item.taxableValue)}</TableCell>
                  <TableCell align="right">{formatCurrency(item.taxAmount)}</TableCell>
                  <TableCell align="right">{formatCurrency(item.total)}</TableCell>
                </TableRow>
              ))}
              
              {/* Total Row */}
              <TableRow 
                sx={{ 
                  backgroundColor: 'rgba(71, 118, 230, 0.05)', 
                  '& .MuiTableCell-root': { fontWeight: 'bold' } 
                }}
              >
                <TableCell colSpan={5} align="right">Total:</TableCell>
                <TableCell align="right">{formatCurrency(totals.totalTaxableValue)}</TableCell>
                <TableCell align="right">{formatCurrency(totals.totalTaxAmount)}</TableCell>
                <TableCell align="right">{formatCurrency(totals.totalAmount)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Amount in Words */}
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" fontStyle="italic" sx={{ color: fontColor }}>
            <strong>Amount in Words:</strong> {numberToWords(Math.round(totals.totalAmount))} Rupees Only
          </Typography>
        </Box>

        {/* Bottom Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {/* Left Side - Payment Details and Terms */}
          <Box sx={{ width: '48%' }}>
            {/* Payment Details */}
            <Box sx={{ mb: 1.5, border: '1px solid #ddd', p: 1, borderRadius: '4px' }}>
              <Typography variant="subtitle2" fontWeight="bold" fontFamily="'Segoe UI', Arial, sans-serif" gutterBottom sx={{ color: fontColor }}>
                Payment Details
              </Typography>
              <Typography variant="body2" sx={{ color: fontColor }}>
                <strong>Bank Name & Branch:</strong> {businessDetails.bankName}, {businessDetails.bankBranch}
              </Typography>
              <Typography variant="body2" sx={{ color: fontColor }}>
                <strong>Bank Account Number:</strong> {businessDetails.accountNumber}
              </Typography>
              <Typography variant="body2" sx={{ color: fontColor }}>
                <strong>IFSC Code:</strong> {businessDetails.ifsc}
              </Typography>
              {businessDetails.upiId && (
                <Typography variant="body2" sx={{ color: fontColor }}>
                  <strong>UPI ID:</strong> {businessDetails.upiId}
                </Typography>
              )}
              {businessDetails.upiQrImage && (
                <Box sx={{ mt: 1, textAlign: 'center' }}>
                  <img 
                    src={businessDetails.upiQrImage} 
                    alt="UPI QR Code" 
                    style={{ maxWidth: '100px', maxHeight: '100px' }} 
                  />
                </Box>
              )}
            </Box>

            {/* Terms & Conditions */}
            <Box sx={{ border: '1px solid #ddd', p: 1, borderRadius: '4px' }}>
              <Typography variant="subtitle2" fontWeight="bold" fontFamily="'Segoe UI', Arial, sans-serif" gutterBottom sx={{ color: fontColor }}>
                Terms & Conditions
              </Typography>
              {invoiceDetails.termsAndConditions && invoiceDetails.termsAndConditions.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: '15px' }}>
                  {invoiceDetails.termsAndConditions.map((term, index) => (
                    <li key={index}>
                      <Typography variant="body2" sx={{ color: fontColor }}>{term}</Typography>
                    </li>
                  ))}
                </ul>
              ) : (
                <Typography variant="body2" sx={{ color: fontColor }}>No terms and conditions specified.</Typography>
              )}
            </Box>
          </Box>

          {/* Right Side - Invoice Summary and Signature */}
          <Box sx={{ width: '48%' }}>
            {/* Invoice Summary */}
            <Box sx={{ mb: 1.5, border: '1px solid #ddd', p: 1, borderRadius: '4px' }}>
              <Typography variant="subtitle2" fontWeight="bold" fontFamily="'Segoe UI', Arial, sans-serif" gutterBottom>
                Invoice Summary
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ color: fontColor }}>Total Taxable Value:</Typography>
                <Typography variant="body2" sx={{ color: fontColor }}>{formatCurrency(totals.totalTaxableValue)}</Typography>
              </Box>
              
              {invoiceDetails.discount && invoiceDetails.discount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: fontColor }}>Discount:</Typography>
                  <Typography variant="body2" sx={{ color: fontColor }}>-{formatCurrency(invoiceDetails.discount)}</Typography>
                </Box>
              )}
              
              {totals.additionalChargesTotal > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: fontColor }}>Additional Charges:</Typography>
                  <Typography variant="body2" sx={{ color: fontColor }}>{formatCurrency(totals.additionalChargesTotal)}</Typography>
                </Box>
              )}
              
              {isInterState ? (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: fontColor }}>IGST:</Typography>
                  <Typography variant="body2" sx={{ color: fontColor }}>{formatCurrency(gstValues.igst)}</Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: fontColor }}>CGST:</Typography>
                    <Typography variant="body2" sx={{ color: fontColor }}>{formatCurrency(gstValues.cgst)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: fontColor }}>SGST:</Typography>
                    <Typography variant="body2" sx={{ color: fontColor }}>{formatCurrency(gstValues.sgst)}</Typography>
                  </Box>
                </>
              )}
              
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                borderTop: '1px solid #ddd', 
                pt: 1, 
                mt: 1,
                fontWeight: 'bold'
              }}>
                <Typography variant="body2" fontWeight="bold" sx={{ color: fontColor }}>Total Invoice Value:</Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ color: fontColor }}>{formatCurrency(totals.totalAmount)}</Typography>
              </Box>
            </Box>

            {/* Signature */}
            <Box sx={{ border: '1px solid #ddd', p: 1, borderRadius: '4px', height: '100px', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" fontWeight="bold" fontFamily="'Segoe UI', Arial, sans-serif" gutterBottom sx={{ color: fontColor }}>
                For {businessDetails.name}
              </Typography>
              <Box sx={{ flexGrow: 1 }} /> {/* Space for signature */}
              <Box sx={{ borderTop: '1px solid #ddd', pt: 1, mt: 'auto' }}>
                <Typography variant="body2" align="center" sx={{ color: fontColor }}>Authorized Signatory</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Footer Section with positioning at bottom */}
      <Box sx={{ 
        marginTop: 'auto',
        textAlign: 'center',
        borderTop: `1px solid ${primaryColor}30`,
        pt: 1.5,
        pb: 1.5,
        backgroundColor: '#f9f9f9',
      }}>
        {/* Thank You Message */}
        <Typography 
          variant="body1" 
          fontWeight="bold" 
          sx={{ 
            color: primaryColor,
            mb: 0.5
          }}
        >
          Thank you for your business!
        </Typography>

        {/* Document Notice */}
        <Typography 
          variant="caption"
          sx={{ 
            color: fontColor,
            fontSize: '0.7rem',
            display: 'block'
          }}
        >
          This is a computer-generated invoice. No signature required.
        </Typography>
      </Box>
    </InvoicePaper>
  );
};

export default AkMoInvoicingFormat; 